import React from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';
import HubCard from '../components/HubCard';
import { FiCheckCircle, FiCompass, FiTarget, FiStar } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Home = () => {
  const { links, settings } = useApp();

  return (
    <div className="bg-brand-light">
      {/* Hero Section */}
      <section className="bg-brand-green text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-serif font-bold mb-6"
          >
            {settings.home.heroTitle}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl md:text-2xl font-light text-brand-gold/90 mb-8 leading-relaxed"
          >
            {settings.home.heroSubtitle}
          </motion.p>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-white/80 max-w-2xl mx-auto"
          >
            {settings.home.heroDescription}
          </motion.p>
        </div>
      </section>

      {/* Intro Paragraph Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-lg text-gray-700 leading-relaxed italic">
            "{settings.home.introQuote}"
          </p>
        </div>
      </section>

      {/* The Hub (Cards) */}
      <section className="py-12 px-4 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {links.map((link, index) => (
            <HubCard key={link.id} link={link} index={index} />
          ))}
        </div>
      </section>

      {/* Section 3: What you’ll find here */}
      <section className="py-20 px-4 bg-brand-dark text-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 text-brand-gold">
                {settings.home.findHereTitle}
              </h2>
              <p className="text-gray-300 mb-8 text-lg">
                {settings.home.findHereDescription}
              </p>
              <ul className="space-y-4">
                {[
                  "Practical travel guides",
                  "Niche guides for different types of travelers",
                  "Beginner-friendly travel business resources",
                  "Helpful tools and next-step content",
                  "Clear entry points instead of information overload"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <SafeIcon icon={FiCheckCircle} className="text-brand-gold shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-brand-green/20 p-8 rounded-3xl border border-brand-gold/10 text-center">
              <SafeIcon icon={FiCompass} className="text-6xl text-brand-gold mx-auto mb-6 opacity-50" />
              <p className="text-xl font-serif italic text-white/90 leading-relaxed">
                "Helping you move from inspiration to action."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Who this is for */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-12 text-brand-dark">
            {settings.home.whoIsForTitle}
          </h2>
          <div className="grid sm:grid-cols-2 gap-6 text-left">
            {[
              "People who love travel and want clearer guidance",
              "Those who enjoy learning before they book",
              "Travelers who want help choosing the right kind of trip",
              "Explorers curious about specific travel styles",
              "Aspiring entrepreneurs exploring travel as a business"
            ].map((text, i) => (
              <div key={i} className="p-6 rounded-xl bg-brand-light border border-gray-100 flex gap-4">
                <SafeIcon icon={FiTarget} className="text-brand-green text-xl shrink-0 mt-1" />
                <p className="text-gray-700 font-medium">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5: Simple trust section */}
      <section className="py-24 px-4 bg-brand-light border-t border-brand-gold/20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="mb-8 flex justify-center">
            <SafeIcon icon={FiStar} className="text-4xl text-brand-gold" />
          </div>
          <h2 className="text-3xl font-serif font-bold mb-8 text-brand-dark">A Practical First Step</h2>
          <p className="text-lg text-gray-600 mb-10 leading-relaxed">
            If you came here from YouTube, this page is designed to help you move from inspiration to action. You do not need to do everything at once. You just need a starting point. 
          </p>
          <div className="space-y-4">
            <p className="font-serif text-brand-green text-xl">Choose the guide or tool that makes the most sense for you, and take the next step from there.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;